import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals } from '../../color-palette/colors';
import { FeatureErrorState } from '../FeatureErrorBoundary';
import { visuallyHiddenSx } from '../Fields/fieldStyles';

import { fillColumnSx, PROSE_MAX } from './screenLayout';

const pausedSx = { color: Neutrals[600], maxWidth: PROSE_MAX } as const;

const PausedNote: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Typography variant="caption2" sx={pausedSx}>
      {t('common.waitingForConnection', 'Waiting for the connection to come back…')}
    </Typography>
  );
};

export interface LoadingStateProps {
  label: string;
  paused?: boolean;
  fallback?: React.ReactNode;
}

export const LoadingState: React.FC<LoadingStateProps> = ({ label, paused = false, fallback }) => {
  if (fallback !== undefined && fallback !== null) {
    return (
      <Stack spacing={3}>
        <Box component="span" sx={visuallyHiddenSx}>
          {label}
        </Box>
        {paused && <PausedNote />}
        {fallback}
      </Stack>
    );
  }
  return (
    <Stack spacing={3} alignItems="center" sx={{ py: 8, textAlign: 'center' }}>
      <CircularProgress aria-label={label} />
      {paused && <PausedNote />}
    </Stack>
  );
};

export interface LoadErrorStateProps {
  title: string;
  reason?: string | null;
  retrying?: boolean;
  onRetry: () => void;
}

export const LoadErrorState: React.FC<LoadErrorStateProps> = ({ title, reason, retrying = false, onRetry }) => {
  const { t } = useTranslation();
  return (
    <FeatureErrorState
      align="center"
      title={title}
      body={reason ?? undefined}
      retryLabel={t('common.tryAgain', 'Try again')}
      retrying={retrying}
      onRetry={onRetry}
    />
  );
};

export interface LoadState {
  hasData: boolean;
  isError: boolean;
  isPaused: boolean;
  isRetrying: boolean;
  isStale?: boolean;
  loadError: unknown;
  refetch: () => void;
}

export interface ScreenBodyProps {
  load: LoadState;
  errorTitle: string;
  loadingLabel: string;
  errorReason?: (error: unknown) => string | null;
  loadingFallback?: React.ReactNode;
  fill?: boolean;
  children: React.ReactNode;
}

const LoadedOr: React.FC<Omit<ScreenBodyProps, 'fill'>> = ({
  load,
  errorTitle,
  loadingLabel,
  errorReason,
  loadingFallback,
  children,
}) => {
  if (load.hasData) return <>{children}</>;
  if (load.isError) {
    return (
      <LoadErrorState
        title={errorTitle}
        reason={errorReason?.(load.loadError) ?? null}
        retrying={load.isRetrying}
        onRetry={load.refetch}
      />
    );
  }
  return <LoadingState label={loadingLabel} paused={load.isPaused} fallback={loadingFallback} />;
};

const bodySx = { minWidth: 0 } as const;
const filledBodySx = [bodySx, fillColumnSx] as const;

export const ScreenBody: React.FC<ScreenBodyProps> = ({ fill = false, ...props }) => (
  <Box aria-busy={!props.load.hasData && !props.load.isError} sx={fill ? filledBodySx : bodySx}>
    <LoadedOr {...props} />
  </Box>
);
